import { motion } from "framer-motion";
import { IMAGES } from "../../lib/content";
import { Reveal } from "../Reveal";

const EASE = [0.16, 1, 0.3, 1];

export const OurVision = () => {
  return (
    <section
      id="vision"
      data-testid="vision-section"
      className="relative bg-forest text-cream py-32 sm:py-40 lg:py-48 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        {/* Intro — Commercial Foundation */}
        <div className="max-w-3xl mb-20 sm:mb-24">
          <Reveal>
            <p className="overline text-gold mb-6">Tō Mātou Moemoeā · Our Vision</p>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight leading-[1.05]">
              Building the kaupapa through enterprise.
            </h2>
          </Reveal>
          <div className="mt-10 space-y-6 text-cream/80 font-light text-lg sm:text-xl leading-relaxed">
            <Reveal y={30}>
              <p>
                Ngā Kākano Wāhine intends to begin its commercial journey by
                becoming a stockist of selected Māori, rongoā Māori and
                kaupapa-aligned products. This supports existing producers while
                we build our own commercial foundation.
              </p>
            </Reveal>
            <Reveal y={30} delay={0.05}>
              <p>
                The long-term intention is for our commercial activity to help
                fund development of the wider kaupapa — rather than relying
                entirely on external funding.
              </p>
            </Reveal>
            <Reveal y={30} delay={0.1}>
              <p className="overline text-gold text-[0.7rem] pt-4 leading-relaxed">
                Support Producers → Commerce → Build NKW Capital → Grow the Kaupapa
              </p>
            </Reveal>
          </div>
        </div>

        {/* Development Pathway — 6 phases (plain text, no new component) */}
        <div className="max-w-3xl mb-24 sm:mb-32">
          <Reveal>
            <p className="overline text-gold mb-6">Te Ara Whakawhanake · Development Pathway</p>
          </Reveal>
          <div className="space-y-6">
            {[
              ["Phase 1 — Commerce", "Support and stock selected Māori, rongoā Māori and kaupapa-aligned producers."],
              ["Phase 2 — Build NKW Capital", "Build the commercial foundation required to develop the wider kaupapa."],
              ["Phase 3 — Rongoā Nursery", "Long-term vision to establish an NKW rongoā nursery in Ōtautahi."],
              ["Phase 4 — Business Education + Practical Experience", "Future partnerships with recognised tertiary and business education partners, alongside practical experience."],
              ["Phase 5 — Business Pilots", "Wāhine develop and test business ideas in a supported environment."],
              ["Phase 6 — Independent Enterprise", "The ultimate goal — wāhine building sustainable businesses of their own."],
            ].map(([title, body], i) => (
              <Reveal key={i} delay={i * 0.04} y={20}>
                <div className="border-t border-cream/12 pt-4">
                  <h4 className="font-serif-display text-xl sm:text-2xl font-light text-cream">
                    {title}
                  </h4>
                  <p className="mt-2 text-cream/65 font-light text-base leading-relaxed">
                    {body}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        {/* Rongoā Nursery — Future Vision */}
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          <Reveal>
            <div className="relative aspect-[4/5] overflow-hidden">
              <motion.img
                src={IMAGES.fernLight}
                alt="Native flora — the world of rongoā Māori"
                initial={{ scale: 1.1 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 1.6, ease: EASE }}
                className="h-full w-full object-cover"
              />
              <div className="absolute inset-0 ring-1 ring-inset ring-gold/25" />
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <p className="overline text-gold mb-6">Te Rongoā · A Future Nursery</p>
            <h3 className="font-serif-display text-3xl sm:text-4xl font-light tracking-tight leading-[1.1] mb-8">
              Growing something of our own.
            </h3>
            <div className="space-y-6 text-cream/80 font-light text-lg leading-relaxed">
              <p>
                Our long-term vision includes establishing an Ngā Kākano Wāhine
                rongoā nursery in Ōtautahi Christchurch. In time, the nursery
                could support plant propagation, regeneration, practical
                learning, enterprise development and future NKW products.
              </p>
              <p>
                This is a future vision — grown respectfully alongside mana
                whenua, practitioners, kaumātua and knowledge holders, with
                tikanga at its heart.
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
